import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { FileWithPath } from 'file-selector';
import moment from 'moment';
import api from '.';
import { WorkflowStepWithCheck } from './project-workflows';

export interface CreateWorkflowStep extends WorkflowStepWithCheck {
  dueDate: MaterialUiPickersDate;
}

export interface UpdateWorkflowStep extends WorkflowStepWithCheck {
  dueDate: MaterialUiPickersDate;
  documentWorkflowStepId: number;
}

export interface CreateProjectDocumentWorkflow {
  projectDocumentRevisionId: number;
  name: string;
  linkName: string | null;
  linkUrl: string | null;
  isAutoApproval: boolean;
  isCreatorViewOnlyExternal: boolean;
  isCreatorViewOnlyFinal: boolean;
  projectDocumentWorkflowSteps: CreateWorkflowStep[];
}

export interface CreateProjectDocumentWorkflowWithChecklist
  extends CreateProjectDocumentWorkflow {
  isChecked: boolean;
  checklistLinkUrl: string | null;
  files: FileWithPath[];
}

export interface UpdateProjectDocumentWorkflow
  extends CreateProjectDocumentWorkflow {
  id: number;
  projectDocumentWorkflowSteps: UpdateWorkflowStep[];
}

export interface GetMyTask {
  page: number;
  limit: number;
  today: Date;
}

export async function getProjectDocumentWorkflow(id: number) {
  const { data } = await api.get(`/project-document-workflows/${id}`);
  return data;
}

export async function createProjectDocumentWorkflow(
  documentWorkflow: CreateProjectDocumentWorkflowWithChecklist
) {
  const formData = new FormData();
  const projectDocumentWorkflowSteps =
    documentWorkflow.projectDocumentWorkflowSteps
      .filter((projectWorkflowStep) => projectWorkflowStep.isRequired)
      .map((projectWorkflowStep) => {
        return {
          workflowStepId: projectWorkflowStep.id,
          dueDate: moment(projectWorkflowStep.dueDate)
            .endOf('day')
            .toISOString(),
          userIds: projectWorkflowStep.users.map((user) => user.id)
        };
      });

  documentWorkflow.files.forEach((file) => {
    formData.append('files', file);
  });

  formData.append(
    'projectDocumentRevisionId',
    documentWorkflow.projectDocumentRevisionId.toString()
  );
  formData.append('name', documentWorkflow.name);
  if (documentWorkflow.linkName) {
    formData.append('linkName', documentWorkflow.linkName);
  }
  if (documentWorkflow.linkUrl) {
    formData.append('linkUrl', documentWorkflow.linkUrl);
  }
  formData.append('isAutoApproval', documentWorkflow.isAutoApproval.toString());
  formData.append(
    'isCreatorViewOnlyExternal',
    documentWorkflow.isCreatorViewOnlyExternal.toString()
  );
  formData.append(
    'isCreatorViewOnlyFinal',
    documentWorkflow.isCreatorViewOnlyFinal.toString()
  );
  formData.append(
    'projectDocumentWorkflowSteps',
    JSON.stringify(projectDocumentWorkflowSteps)
  );
  formData.append('isChecked', documentWorkflow.isChecked.toString());
  if (documentWorkflow.checklistLinkUrl) {
    formData.append('checklistLinkUrl', documentWorkflow.checklistLinkUrl);
  }

  const { data } = await api.post(
    '/project-document-workflows/checklists',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function updateProjectDocumentWorkflow(
  documentWorkflow: UpdateProjectDocumentWorkflow
) {
  const updateProjectDocumentWorkflow = {
    id: documentWorkflow.id,
    name: documentWorkflow.name,
    linkName: documentWorkflow.linkName,
    linkUrl: documentWorkflow.linkUrl,
    isAutoApproval: documentWorkflow.isAutoApproval,
    isCreatorViewOnlyExternal: documentWorkflow.isCreatorViewOnlyExternal,
    isCreatorViewOnlyFinal: documentWorkflow.isCreatorViewOnlyFinal,
    projectDocumentWorkflowSteps:
      documentWorkflow.projectDocumentWorkflowSteps.map(
        (projectWorkflowStep) => {
          return {
            projectDocumentWorkflowStepId:
              projectWorkflowStep.documentWorkflowStepId,
            userIds: projectWorkflowStep.users.map((user) => user.id)
          };
        }
      ),
    updatedAt: moment().format().toString()
  };
  const { data } = await api.put(
    `/project-document-workflows/${updateProjectDocumentWorkflow.id}`,
    updateProjectDocumentWorkflow
  );
  return data;
}

export async function stopProjectDocumentWorkflow(id: number) {
  const stopProjectDocumentWorkflow = {
    finishedDate: moment().format().toString()
  };
  const { data } = await api.put(
    `/project-document-workflows/${id}/stop-workflow`,
    stopProjectDocumentWorkflow
  );
  return data;
}

export async function getProjectDocumentWorkflowActionItems(params: GetMyTask) {
  const { data } = await api.get('/project-document-workflows/action-items', {
    params
  });
  return data;
}

export async function getProjectDocumentWorkflowMyDocuments(params: GetMyTask) {
  const { data } = await api.get('/project-document-workflows/my-documents', {
    params
  });
  return data;
}
